import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    CurrencyCappedInput,
    CurrencyDelegatedInput,
    DaysSliderInput,
    PercentageSplitInput,
    RangeAxisInput,
    TierSegmentedInput
} from "@/pages/ProgramPolicies/components/PolicyValueEditor";

import {
    formatPolicyValue,
    type PolicyDefinition,
    rawValueFor,
    validateEditedValue
} from "./policiesMocks";
import { policyUsesValueField, PolicyValueField } from "./PolicyValueField";
import {
    type Tranche,
    TrancheEditor,
    tranchesHaveError
} from "./TrancheEditor";
import { WalletListEditor } from "./WalletListEditor";

/**
 * The uniform shape every per-type editor reports up to the shell. The shell
 * (`PolicyConfigEditor`) is type-agnostic: it only consumes this summary to
 * drive the ConstraintSummary, FormActions, ConfirmModal change lines, and the
 * serialized value passed to `onSave`.
 */
export interface PolicyEditorSummary {
    /** The value string to persist on save. */
    serialized: string;
    /** True when the draft differs from the seeded value. */
    dirty: boolean;
    /** True when the draft is savable (no validation error). */
    valid: boolean;
    /** Drives the red Minimum highlight on ConstraintSummary. */
    minViolated: boolean;
    /** Drives the red Maximum highlight on ConstraintSummary. */
    maxViolated: boolean;
    /**
     * Per-type human-readable diff lines for the confirm modal. `null` means
     * "render the default `value → formattedDraft` single line" (the shell
     * owns that fallback so simple value editors don't repeat it).
     */
    changeLines: string[] | null;
    /** Resets the draft back to its seeded value (the Discard handler). */
    reset: () => void;
    /**
     * An inline validation message shown between the ConstraintSummary and
     * FormActions. `null` for editors that surface errors inline themselves
     * (tranche/wallet/value-field) — only the specialized single-value
     * editors use this, matching the original `error && !isWalletList &&
     * !isTranche && !usesValueField` guard.
     */
    errorLine: string | null;
}

/**
 * Props every per-type editor component receives. The editor owns its own edit
 * state internally (seeded from `policy`), renders its input, and pushes a
 * fresh `PolicyEditorSummary` up via `onSummaryChange` on every change. The
 * shell never reaches into the editor's state — the summary is the only
 * contract.
 */
export interface PolicyEditorProps {
    policy: PolicyDefinition;
    /**
     * Reports the current summary up to the shell, whenever any of it changes
     * (an edit, or a new seed after a save). Must be a stable callback: a new
     * identity re-reports, and a shell that sets state on report would loop.
     */
    onSummaryChange: (summary: PolicyEditorSummary) => void;
    /**
     * Bumped by the shell to request a reset (Discard). The editor resets its
     * internal state whenever this value changes.
     */
    resetSignal: number;
    /**
     * Locked instances render their editor disabled but still mounted (the
     * wallet-list case). Non-wallet locked policies never reach an editor —
     * the shell short-circuits to a read-only view.
     */
    locked: boolean;
}

/**
 * A registry entry: a predicate that decides whether this entry handles a
 * given policy, plus the editor component that handles it. Entries are tried
 * in order; the first `match` wins — so order encodes precedence, exactly like
 * the original `if/else` ladder.
 *
 * Adding a new policy type = adding ONE entry here. The shell, ConstraintSummary,
 * FormActions, and ConfirmModal stay untouched.
 */
export interface PolicyEditorEntry {
    /** Stable id for debugging / keys. */
    id: string;
    /** True when this entry handles the given policy. */
    match: (policy: PolicyDefinition) => boolean;
    /** The self-contained editor for this type. */
    Editor: (props: PolicyEditorProps) => React.ReactElement;
}

// ── Shared helpers ───────────────────────────────────────────────────────

/** Stable-equality check for arrays of primitives (matches the original
 *  `JSON.stringify` comparisons). */
function jsonEqual<T>(a: T, b: T): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Runs `reset` when the shell bumps `resetSignal` (Discard). Only a change of
 * the signal resets: a new `reset` identity (the seed changed, e.g. after a
 * save) must not discard the draft on its own.
 */
function useResetOnSignal(resetSignal: number, reset: () => void): void {
    const handledSignal = useRef(resetSignal);
    useEffect(() => {
        if (handledSignal.current === resetSignal) return;
        handledSignal.current = resetSignal;
        reset();
    }, [resetSignal, reset]);
}

// ── Tranche editor ───────────────────────────────────────────────────────

function trancheKey(t: Tranche): string {
    return `${t.threshold}|${t.boost}`;
}

function TrancheEditorEntry({
    policy,
    onSummaryChange,
    resetSignal
}: PolicyEditorProps): React.ReactElement {
    const constraint = policy.constraint;
    const trancheThresholdUnit =
        policy.typeId === "time-held-tranche" ? "days" : "value";

    const initialTranches = useMemo<Tranche[]>(
        () =>
            policy.typeId === "time-held-tranche"
                ? [
                      { threshold: "30", boost: "1.2" },
                      { threshold: "90", boost: "1.5" },
                      { threshold: "180", boost: "2" }
                  ]
                : [
                      { threshold: "1000", boost: "1.2" },
                      { threshold: "5000", boost: "1.5" },
                      { threshold: "10000", boost: "2" }
                  ],
        [policy.typeId]
    );
    const [tranches, setTranches] = useState<Tranche[]>(initialTranches);

    const rangeMin = constraint.kind === "range" ? constraint.min : 0;
    const rangeMax = constraint.kind === "range" ? constraint.max : 0;
    const isDelegated = policy.permission === "delegated";
    const boostMin = isDelegated ? 0 : rangeMin;
    const boostMax = isDelegated ? Number.POSITIVE_INFINITY : rangeMax;

    const tranchesError = tranchesHaveError(tranches, boostMin, boostMax);
    const tranchesDirty = !jsonEqual(tranches, initialTranches);

    const minViolated = tranches.some(
        (t) => t.boost.trim() !== "" && Number(t.boost) < boostMin
    );
    const maxViolated = tranches.some(
        (t) => t.boost.trim() !== "" && Number(t.boost) > boostMax
    );

    // Confirm-modal change lines — one per added/removed tranche.
    const trancheUnitLabel =
        trancheThresholdUnit.charAt(0).toUpperCase() +
        trancheThresholdUnit.slice(1);
    const changeLines = useMemo(() => {
        const initialKeys = new Set(initialTranches.map(trancheKey));
        const currentKeys = new Set(tranches.map(trancheKey));
        return [
            ...tranches
                .filter(
                    (t) =>
                        t.threshold.trim() !== "" &&
                        t.boost.trim() !== "" &&
                        !initialKeys.has(trancheKey(t))
                )
                .map(
                    (t) =>
                        `Added ${t.threshold} ${trancheUnitLabel} / ${t.boost}x`
                ),
            ...initialTranches
                .filter((t) => !currentKeys.has(trancheKey(t)))
                .map(
                    (t) =>
                        `Removed ${t.threshold} ${trancheUnitLabel} / ${t.boost}x`
                )
        ];
    }, [tranches, initialTranches, trancheUnitLabel]);

    const serialized = `${tranches.length} tranche${
        tranches.length === 1 ? "" : "s"
    }`;

    const reset = useCallback(
        (): void => setTranches(initialTranches),
        [initialTranches]
    );

    useEffect(() => {
        onSummaryChange({
            serialized,
            dirty: tranchesDirty,
            valid: !tranchesError,
            minViolated,
            maxViolated,
            changeLines,
            errorLine: null,
            reset
        });
    }, [
        onSummaryChange,
        serialized,
        tranchesDirty,
        tranchesError,
        minViolated,
        maxViolated,
        changeLines,
        reset
    ]);

    // Discard from the shell.
    useResetOnSignal(resetSignal, reset);

    return (
        <div className="mt-5">
            <TrancheEditor
                tranches={tranches}
                onChange={setTranches}
                thresholdUnit={trancheThresholdUnit}
                boostMin={boostMin}
                boostMax={boostMax}
            />
        </div>
    );
}

// ── Wallet-list (whitelist / blacklist) editor ───────────────────────────

function WalletListEditorEntry({
    policy,
    onSummaryChange,
    resetSignal,
    locked
}: PolicyEditorProps): React.ReactElement {
    const constraint = policy.constraint;
    const walletOptions =
        constraint.kind === "options" ? constraint.options : [];

    const initialListType = useMemo(
        () => policy.value.split(/\s*—\s*/)[0],
        [policy.value]
    );
    const initialWallets = useMemo(
        () => policy.wallets ?? [],
        [policy.wallets]
    );

    const [listType, setListType] = useState(initialListType);
    const [walletList, setWalletList] = useState<string[]>(initialWallets);

    const dirty =
        listType !== initialListType || !jsonEqual(walletList, initialWallets);

    // Confirm-modal change lines — type change + added/removed counts.
    const changeLines = useMemo(() => {
        const initialWalletSet = new Set(initialWallets);
        const currentWalletSet = new Set(walletList);
        const walletsAdded = walletList.filter(
            (w) => w.trim() !== "" && !initialWalletSet.has(w)
        ).length;
        const walletsRemoved = initialWallets.filter(
            (w) => !currentWalletSet.has(w)
        ).length;
        return [
            ...(listType !== initialListType
                ? [`Changed to ${listType.toLowerCase()}`]
                : []),
            ...(walletsAdded > 0
                ? [
                      `Added ${walletsAdded} wallet${
                          walletsAdded === 1 ? "" : "s"
                      }`
                  ]
                : []),
            ...(walletsRemoved > 0
                ? [
                      `Removed ${walletsRemoved} wallet${
                          walletsRemoved === 1 ? "" : "s"
                      }`
                  ]
                : [])
        ];
    }, [listType, walletList, initialListType, initialWallets]);

    const serialized = `${listType} ${walletList.length} wallet${
        walletList.length === 1 ? "" : "s"
    }`;

    const reset = useCallback((): void => {
        setListType(initialListType);
        setWalletList(initialWallets);
    }, [initialListType, initialWallets]);

    useEffect(() => {
        onSummaryChange({
            serialized,
            dirty,
            // Wallet-list validity is purely dirty-gated (matches the original
            // `walletListDirty` primary-disabled expression — no value error).
            valid: true,
            minViolated: false,
            maxViolated: false,
            changeLines,
            errorLine: null,
            reset
        });
    }, [onSummaryChange, serialized, dirty, changeLines, reset]);

    useResetOnSignal(resetSignal, reset);

    return (
        <div className="mt-5">
            <WalletListEditor
                listType={listType}
                onListTypeChange={setListType}
                options={walletOptions}
                wallets={walletList}
                onWalletsChange={setWalletList}
                disabled={locked}
            />
        </div>
    );
}

// ── ValueEditor-backed editor (amount / days / percent via the rail) ───

function ValueFieldEditorEntry({
    policy,
    onSummaryChange,
    resetSignal
}: PolicyEditorProps): React.ReactElement {
    const constraint = policy.constraint;
    const initialRaw = useMemo(
        () => rawValueFor(constraint, policy.value),
        [constraint, policy.value]
    );
    const [draft, setDraft] = useState(initialRaw);

    const rangeMin = constraint.kind === "range" ? constraint.min : 0;
    const rangeMax = constraint.kind === "range" ? constraint.max : 0;
    const noMax =
        constraint.kind === "range" && !Number.isFinite(constraint.max);

    const minViolated = draft.trim() === "" || Number(draft) < rangeMin;
    const maxViolated =
        !noMax && draft.trim() !== "" && Number(draft) > rangeMax;
    const error = validateEditedValue(policy.permission, constraint, draft);
    const dirty = draft !== initialRaw;
    const reset = useCallback((): void => setDraft(initialRaw), [initialRaw]);

    useEffect(() => {
        onSummaryChange({
            serialized: draft,
            dirty,
            valid: error === null,
            minViolated,
            maxViolated,
            // null → shell renders the default `value → formattedDraft` line.
            changeLines: null,
            // The value-field surfaces its own error styling via `invalid`;
            // it never renders the shell's inline error line.
            errorLine: null,
            reset
        });
    }, [onSummaryChange, draft, dirty, error, minViolated, maxViolated, reset]);

    useResetOnSignal(resetSignal, reset);

    return (
        <div className="mt-5">
            <PolicyValueField
                policy={policy}
                value={draft}
                onChange={setDraft}
                invalid={minViolated || maxViolated}
            />
        </div>
    );
}

// ── Specialized single-value editors (multiplier / tier / days / % /
//    amount-capped / amount-delegated) — the original `else` branch. ──

function SpecializedValueEditorEntry({
    policy,
    onSummaryChange,
    resetSignal
}: PolicyEditorProps): React.ReactElement {
    const constraint = policy.constraint;
    const initialRaw = useMemo(
        () => rawValueFor(constraint, policy.value),
        [constraint, policy.value]
    );
    const [draft, setDraft] = useState(initialRaw);

    const rangeMin = constraint.kind === "range" ? constraint.min : 0;
    const rangeMax = constraint.kind === "range" ? constraint.max : 0;

    const error = validateEditedValue(policy.permission, constraint, draft);
    const dirty = draft !== initialRaw;
    const reset = useCallback((): void => setDraft(initialRaw), [initialRaw]);

    useEffect(() => {
        onSummaryChange({
            serialized: draft,
            dirty,
            valid: error === null,
            // Specialized inputs clamp internally, so they never drive the
            // ConstraintSummary violation highlight (matches the original:
            // min/maxViolated were value-field/tranche-only).
            minViolated: false,
            maxViolated: false,
            changeLines: null,
            // The specialized single-value editors are the only branch that
            // rendered the original inline error line.
            errorLine: error,
            reset
        });
    }, [onSummaryChange, draft, dirty, error, reset]);

    useResetOnSignal(resetSignal, reset);

    // Options constraints are handled by the wallet-list entry, which matches
    // first — so by the time the registry reaches this catch-all entry, the
    // constraint is always a range. The guard narrows the union for the
    // unit-keyed branches below (and is unreachable in practice).
    const range = constraint.kind === "range" ? constraint : null;
    const unit = range?.unit;
    const isTier = unit === "tier";

    return (
        <div className="mt-5 max-w-xl flex flex-col gap-4">
            {/* Live value preview — hidden for the tier toggle, which already
                shows its selected value. */}
            {isTier ? null : (
                <div className="flex flex-col gap-1">
                    <Text variant="number-lg" className="text-foreground-base">
                        {draft ? formatPolicyValue(constraint, draft) : "—"}
                    </Text>
                </div>
            )}

            {range && unit === "multiplier" ? (
                <RangeAxisInput
                    draft={draft}
                    onDraftChange={setDraft}
                    floor={range.min}
                    ceiling={range.max}
                    unit="x"
                />
            ) : unit === "tier" ? (
                <TierSegmentedInput
                    draft={draft}
                    onDraftChange={setDraft}
                    min={rangeMin}
                    max={rangeMax}
                />
            ) : range && unit === "days" ? (
                <DaysSliderInput
                    draft={draft}
                    onDraftChange={setDraft}
                    cap={range.max}
                />
            ) : unit === "%" ? (
                <PercentageSplitInput draft={draft} onDraftChange={setDraft} />
            ) : range && policy.permission === "constrained" ? (
                <CurrencyCappedInput
                    draft={draft}
                    onDraftChange={setDraft}
                    cap={range.max}
                    unit={range.unit}
                />
            ) : (
                <CurrencyDelegatedInput
                    draft={draft}
                    onDraftChange={setDraft}
                    unit={range ? range.unit : ""}
                />
            )}
        </div>
    );
}

/**
 * The per-type editor registry. Tried top to bottom; the first `match` wins.
 * Order mirrors the original `if/else` ladder precedence:
 *   1. tranche (category === "tranche")
 *   2. wallet-list (constraint.kind === "options")
 *   3. value-field (policyUsesValueField)
 *   4. specialized single-value editors (the fallthrough)
 *
 * Note: the locked-non-wallet read-only case is handled by the shell BEFORE
 * the registry is consulted, so no entry needs to model it.
 *
 * Add a new policy type by inserting ONE entry here.
 */
export const POLICY_EDITOR_REGISTRY: PolicyEditorEntry[] = [
    {
        id: "tranche",
        match: (policy) => policy.category === "tranche",
        Editor: TrancheEditorEntry
    },
    {
        id: "wallet-list",
        match: (policy) => policy.constraint.kind === "options",
        Editor: WalletListEditorEntry
    },
    {
        id: "value-field",
        match: (policy) => policyUsesValueField(policy),
        Editor: ValueFieldEditorEntry
    },
    {
        id: "specialized",
        match: () => true,
        Editor: SpecializedValueEditorEntry
    }
];

/** Resolve the editor entry for a policy (first match; the catch-all
 *  guarantees a result). */
export function resolvePolicyEditor(
    policy: PolicyDefinition
): PolicyEditorEntry {
    const entry = POLICY_EDITOR_REGISTRY.find((e) => e.match(policy));
    // The final entry matches everything, so this is unreachable — but keeps
    // the return type non-nullable without a non-null assertion.
    return entry ?? POLICY_EDITOR_REGISTRY[POLICY_EDITOR_REGISTRY.length - 1];
}
